// hooks/useAuth.js - COMPLETE FIX FOR TOKEN PERSISTENCE
import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [error, setError] = useState(null);

  const clearAuthData = () => {
    console.log('🗑️ AuthProvider: Clearing all auth data');
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('username');
    localStorage.removeItem('first_name');
    localStorage.removeItem('last_name');
    localStorage.removeItem('email');
    localStorage.removeItem('is_admin');
    localStorage.removeItem('is_first_login');
    apiService.clearToken();
    setUser(null);
    setIsFirstLogin(false);
    setError(null);
  };

  const storeUserData = (userData, token) => {
    console.log('💾 AuthProvider: Storing user data and token');
    
    // Store token with both keys for compatibility
    localStorage.setItem('authToken', token);
    localStorage.setItem('token', token);
    
    // Store user data
    localStorage.setItem('user_id', userData.id.toString());
    localStorage.setItem('username', userData.username);
    localStorage.setItem('first_name', userData.first_name || '');
    localStorage.setItem('last_name', userData.last_name || '');
    localStorage.setItem('email', userData.email || '');
    localStorage.setItem('is_admin', userData.is_admin ? 'true' : 'false');
    localStorage.setItem('is_first_login', userData.is_first_login ? 'true' : 'false');
    
    // Set in API service
    apiService.setToken(token);
    
    // Set in state
    setUser(userData);
    setIsFirstLogin(userData.is_first_login || false);
    
    console.log('✅ AuthProvider: User data and token stored successfully');
  };

  const reconstructUserFromStorage = () => {
    const storedData = {
      id: localStorage.getItem('user_id'),
      username: localStorage.getItem('username'),
      first_name: localStorage.getItem('first_name'),
      last_name: localStorage.getItem('last_name'),
      email: localStorage.getItem('email'),
      is_admin: localStorage.getItem('is_admin') === 'true',
      is_first_login: localStorage.getItem('is_first_login') === 'true'
    };

    // Only return user object if we have essential data
    if (storedData.id && storedData.username) {
      return storedData;
    }
    return null;
  };

  // Initialize authentication state on app start
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('🔍 AuthProvider: Initializing authentication...');
        
        // Check if we have a stored token
        const storedToken = localStorage.getItem('authToken') || localStorage.getItem('token');
        console.log('🔍 AuthProvider: Stored token check:', storedToken ? 'found' : 'not found');
        
        if (!storedToken) {
          console.log('❌ AuthProvider: No token found, user not authenticated');
          setLoading(false);
          return;
        }

        // Set token in API service immediately
        apiService.setToken(storedToken);
        console.log('✅ AuthProvider: Token set in API service');

        // Try to reconstruct user from localStorage first (faster)
        const storedUser = reconstructUserFromStorage();
        if (storedUser) {
          console.log('📦 AuthProvider: User data found in storage, setting state');
          setUser(storedUser);
          setIsFirstLogin(storedUser.is_first_login);
        }

        // Verify token is still valid by making a profile request
        try {
          console.log('🔐 AuthProvider: Verifying token with server...');
          const profileResponse = await apiService.request('/auth/profile');
          
          if (profileResponse && profileResponse.id) {
            console.log('✅ AuthProvider: Token verified, updating user data');
            
            // Update user data with fresh server response
            const userData = {
              id: profileResponse.id,
              username: profileResponse.username,
              first_name: profileResponse.first_name,
              last_name: profileResponse.last_name,
              email: profileResponse.email,
              is_admin: profileResponse.is_admin || false,
              is_first_login: profileResponse.is_first_login || false
            };

            // Store fresh data
            storeUserData(userData, storedToken);
            
          } else {
            throw new Error('Invalid profile response');
          }
          
        } catch (verificationError) {
          console.error('❌ AuthProvider: Token verification failed:', verificationError);
          
          // If token verification fails, clear everything
          clearAuthData();
        }
        
      } catch (error) {
        console.error('❌ AuthProvider: Auth initialization error:', error);
        clearAuthData();
      } finally {
        setLoading(false);
        console.log('✅ AuthProvider: Auth initialization complete');
      }
    };

    initializeAuth();
  }, []);

  const login = async (username, password) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔐 AuthProvider: Starting login process for:', username);
      
      const response = await apiService.login(username, password);
      
      if (response && response.access_token) {
        console.log('✅ AuthProvider: Login successful');
        
        // Handle user data from response
        let userData;
        if (response.user) {
          userData = response.user;
        } else {
          // Fallback: create user object from response
          userData = {
            id: response.user_id || response.id,
            username: response.username || username,
            first_name: response.first_name || '',
            last_name: response.last_name || '',
            email: response.email || '',
            is_admin: response.is_admin || false,
            is_first_login: response.is_first_login || false
          };
        }
        
        // Store everything
        storeUserData(userData, response.access_token);
        
        console.log('✅ AuthProvider: Login process complete');
        return response;
        
      } else {
        throw new Error('Invalid login response - no access token');
      }
      
    } catch (error) {
      console.error('❌ AuthProvider: Login failed:', error);
      setError(error.message || 'Error al iniciar sesión. Verifica tus credenciales.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const setPassword = async (newPassword) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔑 AuthProvider: Setting new password');
      
      const response = await apiService.setPassword(newPassword);
      
      if (response) {
        // Update user state to reflect password has been set
        const updatedUser = {
          ...user,
          is_first_login: false
        };
        
        setUser(updatedUser);
        setIsFirstLogin(false);
        
        // Update localStorage
        localStorage.setItem('is_first_login', 'false');
        
        console.log('✅ AuthProvider: Password set successfully');
        return response;
      } else {
        throw new Error('Invalid response from server');
      }
      
    } catch (error) {
      console.error('❌ AuthProvider: Set password failed:', error);
      setError(error.message || 'Failed to set password. Please try again.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      console.log('🚪 AuthProvider: Logging out');
      
      // Call API logout (but don't fail if it errors)
      await apiService.logout().catch(() => {
        console.log('API logout call failed, but continuing with local logout');
      });
      
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local state
      clearAuthData();
      console.log('✅ AuthProvider: Logout complete');
    }
  };

  const clearError = () => {
    setError(null);
  };

  const updateUser = (userData) => {
    console.log('🔄 AuthProvider: Updating user data');
    setUser(prev => ({ ...prev, ...userData }));
    
    // Also update localStorage
    if (userData.first_name) localStorage.setItem('first_name', userData.first_name);
    if (userData.last_name) localStorage.setItem('last_name', userData.last_name);
    if (userData.email) localStorage.setItem('email', userData.email);
    if (userData.is_admin !== undefined) localStorage.setItem('is_admin', userData.is_admin ? 'true' : 'false');
  };

  const value = {
    user,
    loading,
    isFirstLogin,
    error,
    login,
    setPassword,
    logout,
    clearError,
    updateUser,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};